import { useState, useMemo, useRef, useEffect } from 'react'
import { MIDI_MIN_TEMPO, MIDI_MAX_TEMPO } from '../../config/songbook'
import { playMidiNotes, stopMidiPlayback } from '../../lib/midiPlayback'
import type { MidiElement } from '../../types/songbook'

interface MidiPlayerProps {
  element: MidiElement
  onTempoChange?: (tempo: number) => void
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']

function midiToName(midi: number): string {
  const note = NOTE_NAMES[midi % 12]
  const octave = Math.floor(midi / 12) - 1
  return `${note}${octave}`
}

export function MidiPlayer({ element, onTempoChange }: MidiPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [tempo, setTempo] = useState(element.tempo)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Flatten all notes from all tracks
  const allNotes = useMemo(
    () => element.tracks.flatMap((t) => t.notes),
    [element.tracks]
  )

  // Note range for visualization
  const { minMidi, maxMidi } = useMemo(() => {
    if (allNotes.length === 0) return { minMidi: 60, maxMidi: 72 }
    const midis = allNotes.map((n) => n.midi)
    return {
      minMidi: Math.min(...midis) - 1,
      maxMidi: Math.max(...midis) + 1,
    }
  }, [allNotes])

  const noteRange = maxMidi - minMidi + 1

  // Draw piano roll
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const w = canvas.width
    const h = canvas.height
    const pxPerSec = w / Math.max(element.durationSeconds, 1)
    const noteH = h / noteRange

    // Clear
    ctx.fillStyle = '#1e1e2e'
    ctx.fillRect(0, 0, w, h)

    // Grid lines for each note
    for (let i = 0; i <= noteRange; i++) {
      const y = i * noteH
      ctx.strokeStyle = '#2a2a3e'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, y)
      ctx.lineTo(w, y)
      ctx.stroke()
    }

    // Note labels on the left
    ctx.font = '11px monospace'
    ctx.fillStyle = '#888'
    for (let m = minMidi; m <= maxMidi; m++) {
      const y = (maxMidi - m) * noteH + noteH * 0.7
      const name = midiToName(m)
      if (name.length <= 3) {
        ctx.fillText(name, 2, y)
      }
    }

    // Draw notes
    const colors = ['#3b82f6', '#22c55e', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899']

    element.tracks.forEach((track, trackIdx) => {
      ctx.fillStyle = colors[trackIdx % colors.length]

      for (const note of track.notes) {
        const x = note.time * pxPerSec
        const y = (maxMidi - note.midi) * noteH
        const nw = Math.max(note.duration * pxPerSec, 2)

        ctx.globalAlpha = 0.3 + note.velocity * 0.7
        ctx.fillRect(x, y + 1, nw, noteH - 2)
      }
    })

    ctx.globalAlpha = 1

    // Playback cursor
    if (progress > 0) {
      const cursorX = progress * w
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.moveTo(cursorX, 0)
      ctx.lineTo(cursorX, h)
      ctx.stroke()
    }
  }, [allNotes, element, minMidi, maxMidi, noteRange, progress])

  const handlePlay = async () => {
    if (isPlaying) {
      stopMidiPlayback()
      setIsPlaying(false)
      setProgress(0)
      return
    }

    setIsPlaying(true)
    setProgress(0)

    await playMidiNotes(
      allNotes,
      element.tempo,
      tempo,
      (pos) => setProgress(pos),
      () => {
        setIsPlaying(false)
        setProgress(0)
      },
    )
  }

  const handleTempoChange = (newTempo: number) => {
    const clamped = Math.max(MIDI_MIN_TEMPO, Math.min(MIDI_MAX_TEMPO, newTempo))
    setTempo(clamped)
    if (onTempoChange) onTempoChange(clamped)
  }

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  return (
    <div className="w-full">
      {/* Filename */}
      <p className="mb-1 truncate text-xs text-text-muted">{element.filename}</p>

      {/* Piano roll canvas */}
      <div className="overflow-x-auto rounded border border-surface-lighter scrollbar-none">
        <canvas
          ref={canvasRef}
          width={Math.max(400, element.durationSeconds * 80)}
          height={Math.max(300, noteRange * 20)}
          className="block"
          style={{ height: Math.max(300, Math.min(noteRange * 20, 500)) }}
        />
      </div>

      {/* Controls */}
      <div className="mt-2 flex items-center gap-3">
        {/* Play/Stop */}
        <button
          className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium ${
            isPlaying
              ? 'bg-error text-white'
              : 'bg-primary text-white'
          } active:opacity-80`}
          onClick={handlePlay}
        >
          {isPlaying ? (
            <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> Stop</>
          ) : (
            <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg> Play</>
          )}
        </button>

        {/* Duration */}
        <span className="text-xs text-text-muted">
          {formatTime(progress * element.durationSeconds)} / {formatTime(element.durationSeconds)}
        </span>

        {/* Tempo */}
        <div className="flex items-center gap-1">
          <button
            className="rounded px-1.5 py-0.5 text-xs text-text-muted hover:bg-surface-lighter"
            onClick={() => handleTempoChange(tempo - 5)}
          >
            -
          </button>
          <span className="min-w-[50px] text-center text-xs text-text">
            {tempo} BPM
          </span>
          <button
            className="rounded px-1.5 py-0.5 text-xs text-text-muted hover:bg-surface-lighter"
            onClick={() => handleTempoChange(tempo + 5)}
          >
            +
          </button>
        </div>
      </div>
    </div>
  )
}
